import {
  Observable,
  catchError,
  firstValueFrom,
  map,
  mergeMap,
  of,
  pipe,
  tap,
} from 'rxjs';
import TokenDto from '../dtos/token-dto';
import ErrorMapping from '../mappings/error.mapping';
import TokenMapping from '../mappings/token-mapping';
import Token from '../models/token';
import TokenProvider from '../providers/token-provider';
import Session from '../models/session';
import { AjaxResponse } from 'rxjs/ajax';
import SessionProvider from '../providers/session-provider';
import RequestProvider from 'domains/core-base/request/providers/request-provider';
import RequestMapping from 'domains/core-base/request/mappings/request-mapping';
import Request from 'domains/core-base/request/models/request';
import { REQUEST_CHANNELS } from 'libs/providers/datalayer/constants';
import ChannelProvider from '../providers/channelProvider';
import Authorization from '../models/authorization';

export enum enumErrorChannel {
  'error',
  'idle',
  'success',
}

type InitAppProps = {
  resultCaptcha?: boolean;
  getRequest?: Request;
  token: TokenDto;
  responseSolicitude?: any;
  responseChannel?: enumErrorChannel;
};
class TokenRepository {
  authorization: Authorization = Authorization.getInstance();

  constructor(
    private tokenProvider: TokenProvider,
    private tokenMapping: TokenMapping,
    private errorMapping: ErrorMapping,
    private sessionProvider: SessionProvider,
    private requestMapper: RequestMapping,
    private channelProvider: ChannelProvider,
  ) {}

  generateSession$ = (): Observable<Session> => {
    return this.tokenProvider.generate().pipe(
      map((result: AjaxResponse<any>) => new Token().fromJSON(result.response)),
      map((token: Token) => this.tokenMapping.generateFromJson(token)),
      tap((token) => {
        window.sessionStorage.setItem('token', token.access_token);
        this.authorization.token = token.access_token;
      }),
      catchError(this.errorMapping.throwError),
    );
  };

  validateCaptcha = (): Promise<TokenDto> => {
    const source$ = this.generateSession$().pipe(
      map((result) => result),
      catchError(this.errorMapping.throwError),
    );
    return firstValueFrom(source$);
  };

  getChannelByUrl = (channel = ''): Promise<any> => {
    return firstValueFrom(
      this.sessionProvider.getToken().pipe(
        mergeMap((authorization) =>
          this.channelProvider.getChannelByUrl(channel, authorization),
        ),
        map(({ response }) => response),
        pipe(catchError(() => of({ error: true }))),
      ),
    );
  };

  initRequest = (channel: Nullable<string>): Promise<Request> => {
    const urlParams = new URLSearchParams(window.location.search);
    const currentPath = window.location.pathname.toLowerCase();
    const defaultChannel = currentPath.includes('-experiencia')
      ? REQUEST_CHANNELS.experiencia
      : REQUEST_CHANNELS.pacifico;
    const selectedChannel = channel ?? defaultChannel;
    const fromParam = urlParams.get('from') ?? null;

    const bodyRequest = this.requestMapper.formatRequest({
      channel: selectedChannel,
      fromParam: fromParam,
    });

    return firstValueFrom(
      this.sessionProvider.getToken().pipe(
        map((authorization) => ({
          authorization,
          ...bodyRequest,
        })),
        mergeMap(RequestProvider.get),
        map((result: AjaxResponse<any>) => result.response),
        map(this.requestMapper.getInitialRequestResponse),
      ),
    );
  };

  initApp = async (params: {
    recaptcha?: string;
    codApplication?: string;
    externalWeb?: boolean;
    isSolicitude?: boolean;
    channel: Nullable<string>;
  }): Promise<InitAppProps | null> => {
    let responseChannel = enumErrorChannel.idle;
    let getRequest;
    const token = await this.validateCaptcha();
    if (params.channel) {
      const channel = await this.getChannelByUrl(params.channel);
      responseChannel = enumErrorChannel.success;
      if (channel.error) {
        responseChannel = enumErrorChannel.error;
      }
    }
    getRequest = await this.initRequest(params.channel);
    return {
      resultCaptcha: true,
      token,
      getRequest,
      responseChannel,
    };
  };
}

export const tokenRepository = new TokenRepository(
  new TokenProvider(),
  new TokenMapping(),
  new ErrorMapping(),
  new SessionProvider(),
  new RequestMapping(),
  new ChannelProvider(),
);
